// FassalkartAI - Complete App with All Routes
// Kisan Ka Sabse Samajhdar Saathi — India's most trusted agricultural AI platform

import React, { Suspense, lazy, useEffect } from 'react';
import { checkEnvironment } from './utils/envCheck';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
import { LanguageProvider } from './context/LanguageContext';
import theme from './styles/theme';

// Guards
import ProtectedRoute from './components/guards/ProtectedRoute';
import AdminRoute from './components/guards/AdminRoute';
import CompanyRoute from './components/guards/CompanyRoute';
import PublicRoute from './components/guards/PublicRoute';

// Layouts
import MainLayout from './components/layout/MainLayout';
import AdminLayout from './components/layout/AdminLayout';
import B2BLayout from './components/layout/B2BLayout';
import SeedMapLayout from './components/layout/SeedMapLayout';

// UI
import LoadingScreen from './components/ui/LoadingScreen';
import ErrorBoundary from './components/ui/ErrorBoundary';
import PageNotFound from './components/ui/PageNotFound';

// Wrap a page with per-route error boundary
const withEB = (element, msg) => (
  <ErrorBoundary fallbackMessage={msg}>{element}</ErrorBoundary>
);

// Guard: requires auth + completed onboarding
function RequireProfile({ children }) {
  const { currentUser, userProfile, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!currentUser) return <Navigate to="/login" replace />;
  if (userProfile && userProfile.onboardingComplete === false)
    return <Navigate to="/onboarding" replace />;
  return children;
}

// Guard: requires seed company auth
function RequireCompany({ children }) {
  const { currentUser, userRole, companyProfile, loading } = useAuth();
  if (loading) return <LoadingScreen />;
  if (!currentUser) return <Navigate to="/business/login" replace />;
  if (userRole && userRole !== 'seedCompany') return <Navigate to="/dashboard" replace />;
  if (!companyProfile) return <LoadingScreen />;
  return children;
}

// ─── Lazy-loaded pages ────────────────────────────────────────────────────────


// Public
const Landing    = lazy(() => import('./pages/Landing'));
const Login      = lazy(() => import('./pages/Login'));
const Register   = lazy(() => import('./pages/Register'));
const About      = lazy(() => import('./pages/About'));
const Pricing    = lazy(() => import('./pages/Pricing'));
const Privacy    = lazy(() => import('./pages/PrivacyPolicy'));
const Terms      = lazy(() => import('./pages/Terms'));
const CompanyLanding   = lazy(() => import('./pages/CompanyLanding'));
const CompanyLogin     = lazy(() => import('./pages/CompanyLogin'));
const CompanyRegister  = lazy(() => import('./pages/CompanyRegister'));
const ForgotPassword   = lazy(() => import('./pages/ForgotPassword'));

// SeedMap AI — B2B seed company portal
const SeedMapRegister    = lazy(() => import('./seedmap/pages/CompanyRegister'));
const SeedUpload         = lazy(() => import('./seedmap/pages/SeedUpload'));
const BusinessDashboard  = lazy(() => import('./seedmap/pages/BusinessDashboard'));
const OrderReport        = lazy(() => import('./seedmap/pages/OrderReport'));
const ReportResult       = lazy(() => import('./seedmap/pages/ReportResult'));
const SeedMapPricing     = lazy(() => import('./seedmap/pages/Pricing'));
const FieldApp           = lazy(() => import('./seedmap/pages/FieldApp'));
const AlertsDashboard    = lazy(() => import('./seedmap/pages/AlertsDashboard'));
const FarmerSeedSearch   = lazy(() => import('./seedmap/pages/FarmerSeedSearch'));

// Onboarding
const FarmerOnboarding = lazy(() => import('./pages/onboarding/FarmerOnboarding'));

// Farmer pages
const Dashboard       = lazy(() => import('./pages/Dashboard'));
const MandiPrices     = lazy(() => import('./pages/MandiPrices'));
const LivePrices      = lazy(() => import('./components/LivePriceTable'));
const CropDoctor         = lazy(() => import('./pages/CropDoctor'));
const CombinedDiagnosis  = lazy(() => import('./pages/farmer/CombinedDiagnosis'));
const CompareMandis         = lazy(() => import('./pages/farmer/CompareMandis'));
const InputCostTracker      = lazy(() => import('./pages/farmer/InputCostTracker'));
const InsuranceCalculator   = lazy(() => import('./pages/farmer/InsuranceCalculator'));
const SoilCardInterpreter   = lazy(() => import('./pages/farmer/SoilCardInterpreter'));
const AIChat          = lazy(() => import('./pages/AIChat'));
const FarmerAIChat    = lazy(() => import('./pages/farmer/AIChat'));
const LanguageTest    = lazy(() => import('./components/LanguageTest'));
const Weather         = lazy(() => import('./pages/Weather'));
const Profile         = lazy(() => import('./pages/Profile'));

// New intelligence pages
const MarketIntelligence   = lazy(() => import('./pages/farmer/MarketIntelligence'));
const YieldGapAnalyzer     = lazy(() => import('./pages/farmer/YieldGapAnalyzer'));
const WaterCenter          = lazy(() => import('./pages/farmer/WaterCenter'));
const SchemeMaximizer      = lazy(() => import('./pages/farmer/SchemeMaximizer'));
const Encyclopedia         = lazy(() => import('./pages/farmer/Encyclopedia'));
const SatelliteIntelligence = lazy(() => import('./pages/farmer/SatelliteIntelligence'));
const ClimateRisk          = lazy(() => import('./pages/farmer/ClimateRisk'));
const LivestockAdvisory    = lazy(() => import('./pages/farmer/LivestockAdvisory'));
const FarmPL               = lazy(() => import('./pages/farmer/FarmPL'));
const DecisionEngine       = lazy(() => import('./pages/farmer/DecisionEngine'));
const CropIntelligence     = lazy(() => import('./pages/farmer/CropIntelligence'));
const MarketTools          = lazy(() => import('./pages/farmer/MarketTools'));
const KisanCommunity       = lazy(() => import('./pages/farmer/Community'));
const ServicesGuide        = lazy(() => import('./pages/ServicesGuide'));
const PricingPage          = lazy(() => import('./pages/pricing/PricingPage'));
const Payment              = lazy(() => import('./pages/Payment'));

// Farmer pages
const SoilHealth      = lazy(() => import('./pages/SoilHealth'));
const Advisory        = lazy(() => import('./pages/CropAdvisory'));
const YieldPredict    = lazy(() => import('./pages/YieldPrediction'));
const Irrigation      = lazy(() => import('./pages/Irrigation'));
const Satellite       = lazy(() => import('./pages/SatelliteView'));
const Schemes         = lazy(() => import('./pages/GovtSchemes'));
const Experts         = lazy(() => import('./pages/Experts'));
const Community       = lazy(() => import('./pages/Community'));
const Marketplace     = lazy(() => import('./pages/Marketplace'));
const MarketplaceBuy  = lazy(() => import('./pages/MarketplaceBuy'));
const MarketplaceSell = lazy(() => import('./pages/MarketplaceSell'));
const Contracts       = lazy(() => import('./pages/Contracts'));
const Insurance       = lazy(() => import('./pages/Insurance'));
const Loans           = lazy(() => import('./pages/Loans'));
const Carbon          = lazy(() => import('./pages/Carbon'));
const Rental          = lazy(() => import('./pages/Rental'));
const Cart            = lazy(() => import('./pages/Cart'));
const Checkout        = lazy(() => import('./pages/Checkout'));
const Orders          = lazy(() => import('./pages/Orders'));
const Notifications   = lazy(() => import('./pages/Notifications'));
const Settings        = lazy(() => import('./pages/Settings'));

// Admin pages
const AdminDashboard    = lazy(() => import('./pages/admin/AdminDashboard'));
const FarmerManagement  = lazy(() => import('./pages/admin/FarmerManagement'));
const PriceManagement   = lazy(() => import('./pages/admin/PriceManagement'));
const AdminAnalytics    = lazy(() => import('./pages/admin/Analytics'));
const NotificationCenter = lazy(() => import('./pages/admin/NotificationCenter'));
const AdminCompanies    = lazy(() => import('./pages/admin/CompanyManagement'));
const AdminAI           = lazy(() => import('./pages/admin/AIMonitor'));
const AdminMarketplace  = lazy(() => import('./pages/admin/MarketplaceControl'));
const AdminRevenue      = lazy(() => import('./pages/admin/Revenue'));
const AdminContent      = lazy(() => import('./pages/admin/ContentManagement'));
const AdminAPILogs      = lazy(() => import('./pages/admin/APILogs'));
const AdminSystem       = lazy(() => import('./pages/admin/SystemHealth'));
const AIAnalytics       = lazy(() => import('./pages/admin/AIAnalytics'));
const AdminTranslations = lazy(() => import('./pages/admin/TranslationManager'));
const DiseaseReview     = lazy(() => import('./pages/admin/DiseaseReview'));

// Expert portal
const DiseaseContribute = lazy(() => import('./pages/expert/DiseaseContribute'));

// B2B pages
const B2BDashboard    = lazy(() => import('./pages/b2b/B2BDashboard'));
const B2BExplore      = lazy(() => import('./pages/b2b/B2BExplore'));
const B2BAPI          = lazy(() => import('./pages/b2b/B2BApi'));
const B2BAPIDocs      = lazy(() => import('./pages/b2b/B2BApiDocs'));
const B2BReports      = lazy(() => import('./pages/b2b/B2BReports'));
const B2BAnalytics    = lazy(() => import('./pages/b2b/B2BAnalytics'));
const B2BSubscription = lazy(() => import('./pages/b2b/B2BSubscription'));
const B2BBilling      = lazy(() => import('./pages/b2b/B2BBilling'));
const B2BProfile      = lazy(() => import('./pages/b2b/B2BProfile'));

// ─── App ─────────────────────────────────────────────────────────────────────

const App = () => {
  useEffect(() => {
    const { isReady, missing } = checkEnvironment();
    if (!isReady && process.env.NODE_ENV === 'development') {
      console.error('Setup incomplete. Missing:', missing.join(', '));
    }
  }, []);

  return (
  <BrowserRouter>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <AuthProvider>
          <AppProvider>
            <LanguageProvider>
            <Suspense fallback={<LoadingScreen />}>
              <Routes>

                {/* ── PUBLIC ────────────────────────────────────────────── */}
                <Route path="/" element={<PublicRoute>{withEB(<Landing />, 'Home page load nahi hua')}</PublicRoute>} />
                <Route path="/about" element={withEB(<About />, 'About page load nahi hua')} />
                <Route path="/pricing" element={withEB(<Pricing />, 'Pricing page load nahi hua')} />
                <Route path="/privacy-policy" element={withEB(<Privacy />, 'Privacy policy load nahi hui')} />
                <Route path="/terms" element={withEB(<Terms />, 'Terms page load nahi hua')} />
                <Route path="/company" element={withEB(<CompanyLanding />, 'Company page load nahi hua')} />
                <Route path="/login" element={<PublicRoute>{withEB(<Login />, 'Login page load nahi hua')}</PublicRoute>} />
                <Route path="/register" element={<PublicRoute>{withEB(<Register />, 'Register page load nahi hua')}</PublicRoute>} />
                <Route path="/company/login" element={<PublicRoute>{withEB(<CompanyLogin />, 'Company login load nahi hua')}</PublicRoute>} />
                <Route path="/company/register" element={<PublicRoute>{withEB(<CompanyRegister />, 'Company register load nahi hua')}</PublicRoute>} />
                <Route path="/forgot-password" element={withEB(<ForgotPassword />, 'Forgot password page load nahi hua')} />

                {/* ── SEEDMAP AI — public ──────────────────────────────── */}
                {/* /business/register is NOT behind PublicRoute so logged-in farmers
                    can also register as a seed company (dual-portal flow) */}
                <Route path="/business/register" element={withEB(<SeedMapRegister />, 'SeedMap register load nahi hua')} />
                <Route path="/business/login" element={<PublicRoute>{withEB(<CompanyLogin />, 'SeedMap login load nahi hua')}</PublicRoute>} />

                {/* ── SEEDMAP AI — protected (with sidebar layout) ──────── */}
                <Route element={<RequireCompany><SeedMapLayout /></RequireCompany>}>
                  <Route path="/business"             element={withEB(<BusinessDashboard />, 'Business dashboard load nahi hua')} />
                  <Route path="/business/seed/upload" element={withEB(<SeedUpload />,        'Seed upload load nahi hua')} />
                  <Route path="/business/order"       element={withEB(<OrderReport />,       'Order form load nahi hua')} />
                  <Route path="/business/report/:orderId" element={withEB(<ReportResult />,  'Report result load nahi hua')} />
                  <Route path="/business/field"       element={withEB(<FieldApp />,          'Field app load nahi hua')} />
                  <Route path="/business/alerts"      element={withEB(<AlertsDashboard />,   'Alerts load nahi hui')} />
                </Route>
                {/* Pricing is public — no layout guard */}
                <Route path="/business/pricing" element={withEB(<SeedMapPricing />, 'Pricing load nahi hua')} />

                {/* ── ONBOARDING (no layout, requires auth) ─────────────── */}
                <Route path="/onboarding" element={
                  <ProtectedRoute>
                    {withEB(<FarmerOnboarding />, 'Onboarding load nahi hua')}
                  </ProtectedRoute>
                } />

                {/* ── FARMER (MainLayout) ───────────────────────────────── */}
                <Route element={<RequireProfile><MainLayout /></RequireProfile>}>
                  <Route path="/dashboard" element={withEB(<Dashboard />, 'Dashboard load nahi hua')} />

                  {/* Market Intelligence */}
                  <Route path="/market-intelligence" element={withEB(<MarketIntelligence />, 'Market Intelligence load nahi hua')} />
                  <Route path="/yield-gap" element={withEB(<YieldGapAnalyzer />, 'Yield Gap Analyzer load nahi hua')} />
                  <Route path="/water" element={withEB(<WaterCenter />, 'Water Center load nahi hua')} />
                  <Route path="/scheme-maximizer" element={withEB(<SchemeMaximizer />, 'Scheme Maximizer load nahi hua')} />
                  <Route path="/encyclopedia" element={withEB(<Encyclopedia />, 'Encyclopedia load nahi hua')} />
                  <Route path="/encyclopedia/:cropName" element={withEB(<Encyclopedia />, 'Encyclopedia load nahi hua')} />

                  {/* Phase 2 — Satellite, Climate, Livestock, Farm P&L */}
                  <Route path="/satellite-intelligence" element={withEB(<SatelliteIntelligence />, 'Satellite Intelligence load nahi hua')} />
                  <Route path="/climate-risk" element={withEB(<ClimateRisk />, 'Climate Risk load nahi hua')} />
                  <Route path="/livestock-advisory" element={withEB(<LivestockAdvisory />, 'Livestock Advisory load nahi hua')} />
                  <Route path="/farm-pl" element={withEB(<FarmPL />, 'Farm P&L load nahi hua')} />
                  <Route path="/plans" element={withEB(<PricingPage />, 'Plans page load nahi hua')} />
                  <Route path="/community-hub" element={withEB(<KisanCommunity />, 'Kisan Community load nahi hua')} />
                  <Route path="/decision-engine" element={withEB(<DecisionEngine />, 'Decision Engine load nahi hua')} />
                  <Route path="/crop-intelligence" element={withEB(<CropIntelligence />, 'Crop Intelligence load nahi hua')} />
                  <Route path="/market-tools" element={withEB(<MarketTools />, 'Market Tools load nahi hua')} />
                  <Route path="/payment" element={withEB(<Payment />, 'Payment page load nahi hua')} />

                  {/* Prices & Market */}
                  <Route path="/prices" element={withEB(<LivePrices />, 'Live Prices load nahi hui')} />
                  <Route path="/prices/:cropName" element={withEB(<LivePrices />, 'Live Prices load nahi hui')} />
                  <Route path="/prices/alerts" element={withEB(<LivePrices />, 'Price Alerts load nahi hui')} />
                  <Route path="/live-prices" element={withEB(<LivePrices />, 'Live Prices load nahi hui')} />

                  {/* AI Tools */}
                  <Route path="/chat" element={withEB(<AIChat />, 'AI Chat load nahi hua')} />
                  <Route path="/ai-chat" element={withEB(<FarmerAIChat />, 'AI Chat load nahi hua')} />
                  <Route path="/services" element={withEB(<ServicesGuide />, 'Services Guide load nahi hua')} />
                  <Route path="/help" element={withEB(<ServicesGuide />, 'Help page load nahi hua')} />
                  <Route path="/lang-test" element={withEB(<LanguageTest />, 'Language Test load nahi hua')} />
                  <Route path="/crop-doctor" element={withEB(<CropDoctor />, 'Crop Doctor load nahi hua')} />
                  <Route path="/crop-doctor/history" element={withEB(<CropDoctor />, 'Crop Doctor load nahi hua')} />
                  <Route path="/combined-diagnosis" element={withEB(<CombinedDiagnosis />, 'Combined Diagnosis load nahi hua')} />
                  <Route path="/expert/disease-contribute" element={withEB(<DiseaseContribute />, 'Disease contribute load nahi hua')} />
                  <Route path="/compare-mandis" element={withEB(<CompareMandis />, 'Compare Mandis load nahi hua')} />
                  <Route path="/cost-tracker" element={withEB(<InputCostTracker />, 'Cost Tracker load nahi hua')} />
                  <Route path="/insurance-calculator" element={withEB(<InsuranceCalculator />, 'Insurance Calculator load nahi hua')} />
                  <Route path="/soil-card" element={withEB(<SoilCardInterpreter />, 'Soil Card Interpreter load nahi hua')} />
                  <Route path="/seeds" element={withEB(<FarmerSeedSearch />, 'Seed search load nahi hua')} />
                  <Route path="/seeds/:crop" element={withEB(<FarmerSeedSearch />, 'Seed search load nahi hua')} />
                  <Route path="/weather" element={withEB(<Weather />, 'Weather page load nahi hua')} />
                  <Route path="/soil" element={withEB(<SoilHealth />, 'Soil Health load nahi hua')} />
                  <Route path="/soil/new" element={withEB(<SoilHealth />, 'Soil Health load nahi hua')} />
                  <Route path="/advisory" element={withEB(<Advisory />, 'Crop Advisory load nahi hua')} />
                  <Route path="/yield" element={withEB(<YieldPredict />, 'Yield Prediction load nahi hua')} />
                  <Route path="/irrigation" element={withEB(<Irrigation />, 'Irrigation page load nahi hua')} />
                  <Route path="/satellite" element={withEB(<Satellite />, 'Satellite View load nahi hua')} />

                  {/* Govt & Community */}
                  <Route path="/schemes" element={withEB(<Schemes />, 'Govt Schemes load nahi hui')} />
                  <Route path="/schemes/:schemeId" element={withEB(<Schemes />, 'Scheme details load nahi hui')} />
                  <Route path="/experts" element={withEB(<Experts />, 'Experts page load nahi hua')} />
                  <Route path="/experts/:expertId" element={withEB(<Experts />, 'Expert details load nahi hua')} />
                  <Route path="/community" element={withEB(<Community />, 'Community load nahi hua')} />
                  <Route path="/community/post/:postId" element={withEB(<Community />, 'Post load nahi hua')} />

                  {/* Marketplace */}
                  <Route path="/marketplace" element={withEB(<Marketplace />, 'Marketplace load nahi hua')} />
                  <Route path="/marketplace/buy" element={withEB(<MarketplaceBuy />, 'Marketplace load nahi hua')} />
                  <Route path="/marketplace/buy/:productId" element={withEB(<MarketplaceBuy />, 'Product details load nahi hua')} />
                  <Route path="/marketplace/sell" element={withEB(<MarketplaceSell />, 'Sell page load nahi hua')} />
                  <Route path="/marketplace/sell/new" element={withEB(<MarketplaceSell />, 'Sell page load nahi hua')} />
                  <Route path="/marketplace/contracts" element={withEB(<Contracts />, 'Contracts load nahi hui')} />
                  <Route path="/marketplace/contracts/new" element={withEB(<Contracts />, 'Contract form load nahi hua')} />

                  {/* Equipment Rental */}
                  <Route path="/rental" element={withEB(<Rental />, 'Rental page load nahi hua')} />
                  <Route path="/rental/list" element={withEB(<Rental />, 'Rental list load nahi hua')} />
                  <Route path="/rental/bookings" element={withEB(<Rental />, 'Bookings load nahi hui')} />

                  {/* Financial */}
                  <Route path="/insurance" element={withEB(<Insurance />, 'Insurance page load nahi hua')} />
                  <Route path="/insurance/apply" element={withEB(<Insurance />, 'Insurance apply load nahi hua')} />
                  <Route path="/insurance/claim" element={withEB(<Insurance />, 'Insurance claim load nahi hua')} />
                  <Route path="/loans" element={withEB(<Loans />, 'Loans page load nahi hua')} />
                  <Route path="/loans/apply" element={withEB(<Loans />, 'Loan apply load nahi hua')} />
                  <Route path="/carbon" element={withEB(<Carbon />, 'Carbon Credits load nahi hua')} />
                  <Route path="/carbon/enroll" element={withEB(<Carbon />, 'Carbon enroll load nahi hua')} />

                  {/* Orders & Cart */}
                  <Route path="/cart" element={withEB(<Cart />, 'Cart load nahi hua')} />
                  <Route path="/checkout" element={withEB(<Checkout />, 'Checkout load nahi hua')} />
                  <Route path="/orders" element={withEB(<Orders />, 'Orders load nahi hui')} />
                  <Route path="/orders/:orderId" element={withEB(<Orders />, 'Order details load nahi hua')} />

                  {/* Profile & Settings */}
                  <Route path="/profile" element={withEB(<Profile />, 'Profile load nahi hua')} />
                  <Route path="/profile/edit" element={withEB(<Profile />, 'Profile edit load nahi hua')} />
                  <Route path="/profile/farm" element={withEB(<Profile />, 'Farm profile load nahi hua')} />
                  <Route path="/notifications" element={withEB(<Notifications />, 'Notifications load nahi hui')} />
                  <Route path="/settings" element={withEB(<Settings />, 'Settings load nahi hua')} />
                  <Route path="/settings/language" element={withEB(<Settings />, 'Language settings load nahi hua')} />
                  <Route path="/settings/subscription" element={withEB(<Settings />, 'Subscription settings load nahi hua')} />
                </Route>

                {/* ── ADMIN (AdminLayout) ───────────────────────────────── */}
                <Route element={<AdminRoute><AdminLayout /></AdminRoute>}>
                  <Route path="/admin" element={withEB(<AdminDashboard />, 'Admin dashboard load nahi hua')} />
                  <Route path="/admin/farmers" element={withEB(<FarmerManagement />, 'Farmer management load nahi hua')} />
                  <Route path="/admin/farmers/:userId" element={withEB(<FarmerManagement />, 'Farmer details load nahi hua')} />
                  <Route path="/admin/prices" element={withEB(<PriceManagement />, 'Price management load nahi hua')} />
                  <Route path="/admin/prices/sync" element={withEB(<PriceManagement />, 'Price sync load nahi hua')} />
                  <Route path="/admin/companies" element={withEB(<AdminCompanies />, 'Company management load nahi hua')} />
                  <Route path="/admin/companies/:companyId" element={withEB(<AdminCompanies />, 'Company details load nahi hua')} />
                  <Route path="/admin/ai" element={withEB(<AdminAI />, 'AI monitor load nahi hua')} />
                  <Route path="/admin/marketplace" element={withEB(<AdminMarketplace />, 'Marketplace control load nahi hua')} />
                  <Route path="/admin/marketplace/products" element={withEB(<AdminMarketplace />, 'Products load nahi hui')} />
                  <Route path="/admin/marketplace/listings" element={withEB(<AdminMarketplace />, 'Listings load nahi hui')} />
                  <Route path="/admin/marketplace/orders" element={withEB(<AdminMarketplace />, 'Orders load nahi hui')} />
                  <Route path="/admin/revenue" element={withEB(<AdminRevenue />, 'Revenue page load nahi hua')} />
                  <Route path="/admin/notifications" element={withEB(<NotificationCenter />, 'Notification center load nahi hua')} />
                  <Route path="/admin/disease-review" element={withEB(<DiseaseReview />, 'Disease review load nahi hua')} />
                  <Route path="/admin/content" element={withEB(<AdminContent />, 'Content management load nahi hua')} />
                  <Route path="/admin/content/schemes" element={withEB(<AdminContent />, 'Schemes content load nahi hua')} />
                  <Route path="/admin/content/crops" element={withEB(<AdminContent />, 'Crops content load nahi hua')} />
                  <Route path="/admin/content/experts" element={withEB(<AdminContent />, 'Experts content load nahi hua')} />
                  <Route path="/admin/content/blog" element={withEB(<AdminContent />, 'Blog content load nahi hua')} />
                  <Route path="/admin/translations" element={withEB(<AdminTranslations />, 'Translation manager load nahi hua')} />
                  <Route path="/admin/api-logs" element={withEB(<AdminAPILogs />, 'API logs load nahi hua')} />
                  <Route path="/admin/system" element={withEB(<AdminSystem />, 'System health load nahi hua')} />
                  <Route path="/admin/analytics" element={withEB(<AdminAnalytics />, 'Analytics load nahi hua')} />
                  <Route path="/admin/ai-analytics" element={withEB(<AIAnalytics />, 'AI analytics load nahi hua')} />
                </Route>

                {/* ── B2B (B2BLayout) ──────────────────────────────────── */}
                <Route element={<CompanyRoute><B2BLayout /></CompanyRoute>}>
                  <Route path="/b2b/dashboard" element={withEB(<B2BDashboard />, 'B2B dashboard load nahi hua')} />
                  <Route path="/b2b/explore" element={withEB(<B2BExplore />, 'Data explorer load nahi hua')} />
                  <Route path="/b2b/explore/:dataset" element={withEB(<B2BExplore />, 'Dataset load nahi hua')} />
                  <Route path="/b2b/api" element={withEB(<B2BAPI />, 'API page load nahi hua')} />
                  <Route path="/b2b/api/docs" element={withEB(<B2BAPIDocs />, 'API docs load nahi hua')} />
                  <Route path="/b2b/reports" element={withEB(<B2BReports />, 'Reports load nahi hui')} />
                  <Route path="/b2b/reports/:reportId" element={withEB(<B2BReports />, 'Report load nahi hua')} />
                  <Route path="/b2b/analytics" element={withEB(<B2BAnalytics />, 'Analytics load nahi hua')} />
                  <Route path="/b2b/subscription" element={withEB(<B2BSubscription />, 'Subscription load nahi hua')} />
                  <Route path="/b2b/billing" element={withEB(<B2BBilling />, 'Billing load nahi hua')} />
                  <Route path="/b2b/profile" element={withEB(<B2BProfile />, 'B2B profile load nahi hua')} />
                </Route>

                {/* ── FALLBACK ──────────────────────────────────────────── */}
                <Route path="*" element={<PageNotFound />} />

              </Routes>
            </Suspense>
            </LanguageProvider>

            {/* Recaptcha container for phone auth */}
            <div id="recaptcha-container" />

            {/* Global Toast Notifications */}
            <ToastContainer
              position="top-right"
              autoClose={3500}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              pauseOnHover
              theme="light"
              toastStyle={{ borderRadius: 12, fontFamily: 'Poppins, sans-serif' }}
            />
          </AppProvider>
        </AuthProvider>
      </ErrorBoundary>
    </ThemeProvider>
  </BrowserRouter>
  );
};

export default App;
